import * as React from "react";
import {useContext, useEffect, useMemo, useState} from "react";

import {Button} from "primereact/button";
import {Chart} from "primereact/chart";
import {ProgressSpinner} from "primereact/progressspinner";

import styles from "./PerguntaResposta.module.css";

import CustomView from "../../component/CustomView";
import Combo from "../../component/form/combo/Combo";
import {CorResposta, IconeResposta, PerguntaRespostaTipo, RotuloResposta} from "../../model/PerguntaRespostaTipo";
import {PerguntaRespostaResumo} from "../../dto/PerguntaRespostaResumo";
import {MensagemContext} from "../../context/MensagemContext";
import {MensagemContextType} from "../../context/MensagemContextType";
import {Ajax} from "../../utils/Ajax";
import rest from "../../utils/EnvConfig";
import {useKeycloak} from "../../keycloak/useKeycloak";
import {TipoUsuarioKeycloak} from "../../keycloak/TipoUsuarioKeycloak";
import {RoleBasedAccess} from "../../keycloak/RoleBasedAccess";
import SemPermissao from "../erro/SemPermissao";

const LIMITE_CIDADES = 10;
const TODAS_CIDADES = "__todas__";
const SEPARADOR_CIDADE = "|";

function PerguntaRespostaView() {
    const {token} = useKeycloak();
    const {error} = useContext(MensagemContext) as MensagemContextType;
    const [cidadeSelecionada, setCidadeSelecionada] = useState<string>(TODAS_CIDADES);
    const [resumo, setResumo] = useState<PerguntaRespostaResumo>();
    const [carregando, setCarregando] = useState<boolean>(false);

    useEffect(() => {
        pesquisar();
    }, [cidadeSelecionada]);

    const montarFiltroCidade = (): string => {
        if (cidadeSelecionada === TODAS_CIDADES) {
            return "";
        }

        const [cidade, estado] = cidadeSelecionada.split(SEPARADOR_CIDADE);
        return `?cidade=${encodeURIComponent(cidade)}&estado=${encodeURIComponent(estado ?? "")}`;
    }

    const pesquisar = () => {
        setCarregando(true);

        Ajax.of()
            .error(error)
            .setTokenKeycloak(token!)
            .obterTodosCustom(`${rest.perguntaResposta}/${rest.perguntaRespostaResumo}${montarFiltroCidade()}`,
                (data: any) => {
                    setResumo(data as PerguntaRespostaResumo);
                    setCarregando(false);
                },
                () => setCarregando(false));
    }

    const opcoesCidades = useMemo(() => {
        const cidades = (resumo?.cidades ?? []).map(cidade => ({
            chave: `${cidade.cidade}${SEPARADOR_CIDADE}${cidade.estado}`,
            rotulo: `${cidade.cidade}/${cidade.estado}`
        }));

        return [{chave: TODAS_CIDADES, rotulo: "Todas as cidades"}, ...cidades];
    }, [resumo]);

    const obterCor = (resposta: PerguntaRespostaTipo): string => {
        return getComputedStyle(document.documentElement)
            .getPropertyValue(CorResposta[resposta]).trim();
    }

    const totalDaResposta = (resposta: PerguntaRespostaTipo): number => {
        return resumo?.totaisPorResposta?.find(total => total.resposta === resposta)?.total ?? 0;
    }

    const dadosTotaisPorResposta = useMemo(() => {
        const totais = resumo?.totaisPorResposta ?? [];

        return {
            labels: totais.map(total => RotuloResposta[total.resposta]),
            datasets: [
                {
                    data: totais.map(total => total.total),
                    backgroundColor: totais.map(total => obterCor(total.resposta)),
                }
            ]
        };
    }, [resumo]);

    const dadosPorPergunta = useMemo(() => {
        const perguntas = resumo?.perguntas ?? [];

        return {
            labels: perguntas.map(pergunta => pergunta.descricao),
            datasets: Object.values(PerguntaRespostaTipo).map(resposta => ({
                label: RotuloResposta[resposta],
                backgroundColor: obterCor(resposta),
                data: perguntas.map(pergunta =>
                    pergunta.totaisPorResposta.find(total => total.resposta === resposta)?.total ?? 0)
            }))
        };
    }, [resumo]);

    const dadosPorCidade = useMemo(() => {
        const cidades = (resumo?.cidades ?? []).slice(0, LIMITE_CIDADES);

        return {
            labels: cidades.map(cidade => `${cidade.cidade}/${cidade.estado}`),
            datasets: [
                {
                    label: "Respostas",
                    data: cidades.map(cidade => cidade.total),
                    backgroundColor: obterCor(PerguntaRespostaTipo.SIM),
                }
            ]
        };
    }, [resumo]);

    const opcoesRosca = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom"
            }
        }
    };

    const opcoesBarra = {
        indexAxis: "y",
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            }
        }
    };

    const opcoesBarraEmpilhada = {
        indexAxis: "y",
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom"
            }
        },
        scales: {
            x: {
                stacked: true,
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            },
            y: {
                stacked: true
            }
        }
    };

    const conteudo = () => {
        if (carregando) {
            return (
                <center>
                    <ProgressSpinner/>
                </center>
            );
        }

        if (!resumo || resumo.totalGeral === 0) {
            return (
                <div className={styles.semDados}>
                    Nenhuma resposta registrada para o filtro selecionado.
                </div>
            );
        }

        return (
            <>
                <div className={styles.cartoes}>
                    <div className={styles.cartao}>
                        <div className={styles.cartaoTitulo}>
                            <i className={"pi pi-comments"}></i>
                            Total de respostas
                        </div>
                        <div className={styles.cartaoTotal}>{resumo.totalGeral}</div>
                    </div>

                    {Object.values(PerguntaRespostaTipo).map(resposta => (
                        <div key={resposta} className={styles.cartao}>
                            <div className={styles.cartaoTitulo}>
                                <i className={IconeResposta[resposta]}
                                   style={{color: obterCor(resposta)}}></i>
                                {RotuloResposta[resposta]}
                            </div>
                            <div className={styles.cartaoTotal}>{totalDaResposta(resposta)}</div>
                        </div>
                    ))}
                </div>

                <div className={styles.graficos}>
                    <div className={styles.graficoRosca}>
                        <div className={styles.tituloGrafico}>Distribuição das respostas</div>
                        <Chart type="doughnut" data={dadosTotaisPorResposta} options={opcoesRosca}
                               style={{height: "320px"}}/>
                    </div>

                    <div className={styles.graficoPergunta}>
                        <div className={styles.tituloGrafico}>Respostas por pergunta</div>
                        <Chart type="bar" data={dadosPorPergunta} options={opcoesBarraEmpilhada}
                               style={{height: `${Math.max(220, resumo.perguntas.length * 60)}px`}}/>
                    </div>

                    {cidadeSelecionada === TODAS_CIDADES && resumo.cidades.length > 0 && (
                        <div className={styles.graficoCidade}>
                            <div className={styles.tituloGrafico}>Respostas por cidade</div>
                            <Chart type="bar" data={dadosPorCidade} options={opcoesBarra}
                                   style={{height: `${Math.max(160, Math.min(resumo.cidades.length, LIMITE_CIDADES) * 45)}px`}}/>
                        </div>
                    )}
                </div>
            </>
        );
    }

    return (
        <RoleBasedAccess roles={TipoUsuarioKeycloak.ADMIN}
                         forbiddenFallback={<SemPermissao />}>
            <CustomView id={"pergunta-resposta"} title={"Respostas do questionário"}
                        mostrarCancelar={false} mostrarBarraBotoes={false}>
                <div className={styles.filtros}>
                    <div className={styles.filtroCampo}>
                        <Combo id={"cidade"} label={"Cidade"}
                               value={cidadeSelecionada}
                               values={opcoesCidades}
                               optionLabel={"rotulo"}
                               optionValue={"chave"}
                               filter={true}
                               placeHolder={"Selecione a cidade"}
                               onChangeHandler={(e: any) => setCidadeSelecionada(e.value)}/>
                    </div>

                    <div className={styles.filtroBotao}>
                        <Button label={"Atualizar"} icon={"pi pi-refresh"} raised size={"large"}
                                onClick={pesquisar}/>
                    </div>
                </div>

                <div className={styles.espacamento}>
                    {conteudo()}
                </div>
            </CustomView>
        </RoleBasedAccess>
    );
}

export default PerguntaRespostaView;
